// =============================================================================
// hsHighlight (5.20.1) — which parts of a string the Clients search matched, so the list and its drop-down can
// wrap them in <mark>. It matches the way the server's search does (people.query_tokens / norm_text):
//   - case- and accent-insensitive ("jose" marks "JOSÉ");
//   - every search word is marked (each one anywhere in the text);
//   - a phone-like entry ("(713) 555-0101", "555-0101") is one digit string, matched against the DIGITS of the
//     text, so it marks the digits inside a formatted phone (from the first matched digit to the last).
// Returns plain string parts — the caller renders them as React text nodes (never as HTML).
// =============================================================================

const fold = (ch) => ch.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();

// The search box text -> {digits: '5550101'} for a phone-like entry, or {words: ['jose', 'alvarez']}.
export function searchTerms(q) {
  const raw = String(q || '').trim();
  if (raw.length < 2) return { words: [] };
  if (/^[\d\s()+.-]+$/.test(raw)) {
    let d = raw.replace(/\D/g, '');
    if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
    return d.length >= 2 ? { digits: d } : { words: [] };
  }
  const words = fold(raw).split(/[^a-z0-9]+/).filter(Boolean).slice(0, 8);
  return { words };
}

// [[start, end)] ranges of `text` to mark, merged and sorted.
export function highlightRanges(text, q) {
  const s = String(text ?? '');
  const t = searchTerms(q);
  const ranges = [];
  if (t.digits) {
    const idx = [];
    let digits = '';
    for (let i = 0; i < s.length; i += 1) if (s[i] >= '0' && s[i] <= '9') { digits += s[i]; idx.push(i); }
    let from = digits.indexOf(t.digits);
    while (from !== -1) {
      ranges.push([idx[from], idx[from + t.digits.length - 1] + 1]);
      from = digits.indexOf(t.digits, from + t.digits.length);
    }
  } else if (t.words && t.words.length) {
    // folded text with a map back to the original indices (one original char may fold to 0..n chars)
    let folded = '';
    const map = [];
    for (let i = 0; i < s.length; i += 1) {
      const f = fold(s[i]);
      for (let k = 0; k < f.length; k += 1) { folded += f[k]; map.push(i); }
    }
    for (const w of t.words) {
      let from = folded.indexOf(w);
      while (from !== -1) {
        let end = map[from + w.length - 1] + 1;
        while (end < s.length && fold(s[end]) === '') end += 1;      // keep a decomposed accent with its letter
        ranges.push([map[from], end]);
        from = folded.indexOf(w, from + w.length);
      }
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]); else merged.push([r[0], r[1]]);
  }
  return merged;
}

// -> [{text, mark}] covering the whole string, in order (concatenating every `text` gives the input back).
export function splitHighlight(text, q) {
  const s = String(text ?? '');
  const out = [];
  let pos = 0;
  for (const [a, b] of highlightRanges(s, q)) {
    if (a > pos) out.push({ text: s.slice(pos, a), mark: false });
    out.push({ text: s.slice(a, b), mark: true });
    pos = b;
  }
  if (pos < s.length || !out.length) out.push({ text: s.slice(pos), mark: false });
  return out;
}

export const hasMatch = (text, q) => highlightRanges(text, q).length > 0;
